import { useState } from 'react';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import styled from 'styled-components';
import {
    GuidedBrandingProductRecommender,
    brandingRouteValid,
    getExchangeRate,
} from '@99designs/feature-guided-recommenders';
import { CurrencyCode } from '@99designs/i18n';
import { Site } from '@99designs/site-navigation/lib/Site';
import DefaultPageLayout from '../../../components/common/DefaultPageLayout';
import { Head } from '../../../components/common/Head/';
import { getDefaultPageProps } from '../../../lib/getDefaultPageProps';
import type { SiteConfig } from '@99designs/site-navigation/lib/Site';

const PageContainer = styled.div`
    min-height: calc(100vh - var(--viewport-offset, 0px));
    display: flex;
    flex-direction: column;
    justify-content: var(--justify-content, space-between);
`;

export default function GuidedBrandingRecommenderPage({
    siteConfig,
    userRole,
    exchangeRate,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
    const site = new Site(JSON.parse(siteConfig) as SiteConfig);
    const [headTitle, setHeadTitle] = useState<string>(__('Compare branding design solutions'));
    const [headDescription, setHeadDescription] = useState<string>('');

    const onPageChange = (title: string, description: string) => {
        setHeadTitle(title);
        setHeadDescription(description);
    };

    return (
        <PageContainer>
            <Head
                title={headTitle}
                description={headDescription}
                currentDomain={site.getDomain()}
                addSEOTags
            />
            <GuidedBrandingProductRecommender
                role={userRole}
                site={site}
                exchangeRate={exchangeRate}
                onPageChange={onPageChange}
                Layout={DefaultPageLayout}
            />
        </PageContainer>
    );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
    if (!brandingRouteValid(context.query)) {
        return {
            notFound: true,
        };
    }

    const defaultPageProps = await getDefaultPageProps(context);

    const site = new Site(JSON.parse(defaultPageProps.siteConfig) as SiteConfig);
    const { currency } = site.siteConfig;

    // Get exchange
    const exchangeRate = await getExchangeRate(context.req, currency as CurrencyCode);

    return {
        props: {
            ...defaultPageProps,
            exchangeRate,
        },
    };
};
